import React, { useState, useEffect, useRef, useCallback } from "react";
import { Container, SuggestionsListContainer } from "./styled";

const SuggestionsList = ({
  show,
  items,
  selectedItems,
  setCollectionName,
  emails,
  setEmails,
  validateEmail,
  addModal,
}) => {
  if (!show || !items?.length) return null;

  const isEmail = (item) => typeof item === "string" && validateEmail(item);

  return (
    <SuggestionsListContainer>
      <ul
        className="suggestions-list"
        style={{
          position: "absolute",
          top: "100%",
          left: 0,
          width: "100%",
          background: "white",
          border: "1px solid #ddd",
          borderRadius: "8px",
          boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
          zIndex: 100,
          marginTop: "4px",
          listStyle: "none",
          padding: "8px 0",
        }}
      >
        {items.map((item) => {
          const name = item.name || item;

          const isCheckedCollection = selectedItems?.includes(name);
          const isCheckedEmail = emails?.includes(name);

          return (
            <li
              key={item.id || item}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "6px 10px",
                cursor: "pointer",
                gap: "10px",
              }}
              onClick={() => {
                if (isEmail(name)) {
                  if (!emails.includes(name)) {
                    setEmails((prev) => [...prev, name]);
                  }
                  return;
                }

                setCollectionName((prev) => {
                  if (prev.includes(name)) {
                    return prev.filter((col) => col !== name);
                  }
                  return [...prev, name];
                });
              }}
            >
              <input
                type="checkbox"
                checked={isEmail(name) ? isCheckedEmail : isCheckedCollection}
                readOnly
                style={{ width: 18, height: 18 }}
              />

              <span
                style={{
                  padding: "4px 10px",
                  borderRadius: "16px",
                  background: isEmail(name)
                    ? isCheckedEmail
                      ? "#007bff22"
                      : "#f1f1f1"
                    : isCheckedCollection
                    ? "#007bff22"
                    : "#f1f1f1",
                  color: isEmail(name)
                    ? isCheckedEmail
                      ? "#0056cc"
                      : "#444"
                    : isCheckedCollection
                    ? "#0056cc"
                    : "#444",
                  fontSize: "14px",
                }}
              >
                {name}
              </span>
            </li>
          );
        })}
      </ul>
    </SuggestionsListContainer>
  );
};

const EmailChipList = ({ emails, onRemove }) => {
  if (!emails?.length) return null;
  return (
    <div className="emails-list">
      {emails.map((email) => (
        <div className="email-chip" key={email}>
          {email}
          <span onClick={() => onRemove(email)}>✕</span>
        </div>
      ))}
    </div>
  );
};

const SharePopup = ({
  isActive,
  popupStep,
  setIsShare,
  onChange,
  onSend,
  isCollection,
  setClipName,
  clipName,
  handleSaveAnalisys,
  clipCollections,
  setCollectionName,
  handleSaveCollection,
  collectionName,
}) => {
  const [state, setState] = useState({
    inputValue: "",
    emails: [],
    relatedAccounts: [],
    showSuggestions: false,
  });

  const inputRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const sessionUser = JSON.parse(sessionStorage.getItem("user"));
    if (sessionUser?.relatedAccounts) {
      setState((prev) => ({
        ...prev,
        relatedAccounts: sessionUser.relatedAccounts,
      }));
    }
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (onChange) {
      onChange(state);
    }
  }, [state.emails, onChange]);

  const validateEmail = useCallback((email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }, []);

  const handleInputChange = useCallback((value) => {
    setState((prev) => ({ ...prev, inputValue: value }));
  }, []);

  const handleKeyDown = useCallback(
    (e) => {
      if (["Enter", ",", " "].includes(e.key)) {
        e.preventDefault();
        const newEmail = state.inputValue.trim().replace(/,$/, "");

        if (
          newEmail &&
          validateEmail(newEmail) &&
          !state.emails.includes(newEmail)
        ) {
          setState((prev) => ({
            ...prev,
            emails: [...prev.emails, newEmail],
            inputValue: "",
          }));
        }
      }

      if (
        e.key === "Backspace" &&
        !state.inputValue &&
        state.emails.length > 0
      ) {
        e.preventDefault();
        setState((prev) => ({
          ...prev,
          emails: prev.emails.slice(0, -1),
        }));
      }
    },
    [state.inputValue, state.emails, validateEmail]
  );

  const handleFocus = useCallback(() => {
    if (state.relatedAccounts.length > 0) {
      setState((prev) => ({ ...prev, showSuggestions: true }));
    }
  }, [state.relatedAccounts.length]);

  const handleBlur = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setState((prev) => ({ ...prev, showSuggestions: false }));
    }, 150);
  }, []);

  const handleSelectEmail = useCallback((email) => {
    const emailString = email.name || email;

    setState((prev) => ({
      ...prev,
      emails: prev.emails.includes(emailString)
        ? prev.emails
        : [...prev.emails, emailString],
      inputValue: "",
      showSuggestions: false,
    }));
  }, []);

  const handleRemoveEmail = useCallback((email) => {
    setState((prev) => ({
      ...prev,
      emails: prev.emails.filter((e) => e !== email),
    }));
  }, []);

  const handleCollectionBlur = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setState((prev) => ({ ...prev, showSuggestions: false }));
    }, 150);
  }, []);

  return (
    <Container className={`share-popup ${isActive ? "show" : "hide"}`}>
      <div className="popup">
        <button
          className="btn-primary ghost no-print closePopup"
          onClick={() => setIsShare(false)}
          aria-label="Cerrar"
        >
          X
        </button>

        {popupStep === "isShare" && (
          <>
            <h2>Compartir {isCollection ? "colección" : "tablero"}</h2>
            <p>
              Invita a otros a ver este análisis. Necesitan una cuenta gratuita
              en <strong>Deportáctico</strong>.
            </p>

            <div
              className="email-input-container"
              style={{ position: "relative" }}
            >
              <input
                ref={inputRef}
                placeholder="Ingresa correos separados por coma"
                value={state.inputValue}
                onChange={(e) => handleInputChange(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={handleFocus}
                onBlur={handleBlur}
                type="text"
              />

              <SuggestionsList
                show={state.showSuggestions}
                items={state.relatedAccounts}
                selectedItems={collectionName}
                setCollectionName={setCollectionName}
                emails={state.emails}
                setEmails={(fn) =>
                  setState((prev) => ({ ...prev, emails: fn(prev.emails) }))
                }
                inputValue={state.inputValue}
                setInputValue={(v) =>
                  setState((prev) => ({ ...prev, inputValue: v }))
                }
                validateEmail={validateEmail}
              />

              <button
                className="btn-primary"
                onClick={() => onSend(state.emails)}
                disabled={state.emails.length === 0}
              >
                Enviar invitaciones
              </button>
            </div>

            <p className="bold">Correos de los invitados</p>
            <EmailChipList emails={state.emails} onRemove={handleRemoveEmail} />
          </>
        )}

        {popupStep?.startsWith("save-") && (
          <>
            {popupStep.includes("isSetClipName") && (
              <>
                <h2>Guardar clip</h2>
                <h4>Seleccionar nombre</h4>

                <div className="email-input-container">
                  <input
                    placeholder="Escribe el nombre del clip"
                    value={clipName}
                    onChange={(e) => setClipName(e.target.value)}
                    type="text"
                  />
                </div>
              </>
            )}

            {popupStep.includes("isSetCollection") && (
              <h2>Guardar en colección</h2>
            )}

            <>
              <h4>Seleccionar colección</h4>
              <div
                className="email-input-container"
                style={{ position: "relative" }}
              >
                <input
                  placeholder="Crear colección"
                  value={collectionName[0] || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCollectionName(value ? [value] : []);
                  }}
                  onFocus={handleFocus}
                  onBlur={handleCollectionBlur}
                  type="text"
                />

                <SuggestionsList
                  show={state.showSuggestions}
                  items={clipCollections}
                  selectedItems={collectionName}
                  onSelect={setCollectionName}
                  setCollectionName={setCollectionName}
                  validateEmail={validateEmail}
                />

                {popupStep.includes("isSetCollection") && (
                  <button
                    className="btn-primary"
                    onClick={(e) => {
                      handleSaveCollection(
                        state.inputValue !== "" ? state.inputValue : null
                      );
                    }}
                  >
                    Guardar
                  </button>
                )}
              </div>

              {collectionName.map((collection) => (
                <div className="emails-list" key={collection}>
                  <div className="email-chip">
                    {collection}

                    <span
                      onClick={() => {
                        setCollectionName((prev) =>
                          prev.filter((c) => c !== collection)
                        );

                        handleInputChange("");
                      }}
                      style={{ cursor: "pointer", marginLeft: 6 }}
                    >
                      ✕
                    </span>
                  </div>
                </div>
              ))}
            </>

            {popupStep.includes("isSetClipName") && (
              <div className="email-input-container">
                <button
                  className="btn-primary"
                  onClick={(e) => {
                    handleSaveAnalisys(
                      state.inputValue !== "" ? state.inputValue : null
                    );
                  }}
                  style={{ marginTop: "1rem" }}
                >
                  Guardar
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </Container>
  );
};

export default SharePopup;
